import {Dialog, Transition} from '@headlessui/react';
import {GoogleMap, MarkerF, useLoadScript} from '@react-google-maps/api';
import clsx from 'clsx';
import {Fragment, useState} from 'react';
import {Link} from 'react-router-dom';
import crossIcon from '../../../../../../assets/images/icons/cross.svg';
import leftArrow from '../../../../../../assets/images/layout/left-arrow.svg';
import {OnClose} from '../../../../../common/components/modals/Types';
import {useConfig} from '../../../../../common/providers/ConfigProvider';
import {useI18n} from '../../../../../common/providers/I18nProvider';
import {isMobile} from '../../../common/Helpers';
import {SelectedLocation} from '../Types';
import {PlacesAutocomplete} from './PlacesAutocomplete';

const defaultProps = {
	center: {
		lat: 35.684575094889574,
		lng: 139.76824858071498,
	},
	zoom: 11,
};

interface Props {
	onSelect: (location: SelectedLocation) => void;
	preSelectedLocation?: SelectedLocation;
}

export default function MapModal({show, close, onSelect, preSelectedLocation}: OnClose & Props) {
	const {mapKey} = useConfig();
	const {isLoaded} = useLoadScript({
		googleMapsApiKey: mapKey,
		libraries: ['places'],
	});
	const [selected, setSelected] = useState<SelectedLocation | undefined>(preSelectedLocation);
	const {i18n} = useI18n();

	return (
		<Transition appear show={show} as={Fragment}>
			<Dialog as='div' className='relative z-40' onClose={close}>
				<Transition.Child
					as={Fragment}
					enter='ease-out duration-300'
					enterFrom='opacity-0'
					enterTo='opacity-100'
					leave='ease-in duration-200'
					leaveFrom='opacity-100'
					leaveTo='opacity-0'
				>
					<div className='fixed inset-0 bg-black bg-opacity-25' />
				</Transition.Child>

				<div className='fixed inset-0 overflow-y-auto'>
					<div className='flex min-h-full items-center justify-center text-center '>
						<Transition.Child
							as={Fragment}
							enter='ease-out duration-300'
							enterFrom='opacity-0 scale-95'
							enterTo='opacity-100 scale-100'
							leave='ease-in duration-200'
							leaveFrom='opacity-100 scale-100'
							leaveTo='opacity-0 scale-95'
						>
							<Dialog.Panel className='w-full sm:w-auto sm-modalheight transform overflow-hidden sm:rounded-2xl bg-white text-left align-middle shadow-xl transition-all'>
								{!isMobile() && (
									<div className='flex items-center justify-between py-6 px-4'>
										<h3 className='text-xl text-black-10 font-bold'>{i18n('AUTH.PROFILE.MAP.TITLE')}</h3>
										<button
											className='p-1 ml-auto border-0 text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none'
											onClick={close}
										>
											<img src={crossIcon} alt='close modal' />
										</button>
									</div>
								)}
								{isMobile() && (
									<div className='flex py-4 border-b-2 ps-6 mb-4 z-[100] relative'>
										<span className='pr-4 pt-3.5'>
											<Link
												onClick={() => {
													window.history.back();
												}}
												to='/'
											>
												<img src={leftArrow} alt='' />
											</Link>
										</span>
										<span>
											<PlacesAutocomplete setSelected={setSelected} />
										</span>
									</div>
								)}

								{isLoaded && (
									<div className='flex-auto'>
										<div className='absolute top-22 left-4 z-50 hidden sm:block'>
											<PlacesAutocomplete setSelected={setSelected} />
										</div>
										{/* <div className='flex justify-center'> */}
										<GoogleMap
											zoom={defaultProps.zoom}
											center={selected ? selected.geo : defaultProps.center}
											mapContainerClassName={`map-container ${isMobile() ? 'map-mbcontainer' : ''}`}
											options={{mapTypeControl: false}}
										>
											{selected && <MarkerF position={selected.geo} />}
										</GoogleMap>
										{/* </div> */}
									</div>
								)}

								<div className='mt-6 mb-7 flex justify-center'>
									<button
										type='button'
										className={clsx(
											'inline-flex justify-center border border-transparent bg-primary px-28 py-4 font-medium text-white rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
											{
												'opacity-40 cursor-not-allowed': selected === undefined,
											},
										)}
										onClick={() => {
											if (selected !== undefined) onSelect(selected);
										}}
									>
										{i18n('AUTH.PROFILE.MAP.BUTTON.SET')}
									</button>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
